
import { 
  auth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  db,
  doc,
  setDoc,
  getDoc
} from '@/firebase';

export type UserType = 'individual' | 'company';

export interface User {
  uid: string;
  email: string;
  name: string;
  type: UserType;
  agentName?: string;
  createdAt: string;
}

class AuthService {
  /**
   * Attempt to sign in using Firebase
   */
  async signIn(email: string, password: string): Promise<{ user: any }> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Fetch user profile to get the type
      const profileDoc = await getDoc(doc(db, 'users', user.uid));
      const profileData = profileDoc.exists() ? profileDoc.data() : null;
      
      return { 
        user: { 
          ...user, 
          type: profileData?.userType || 'individual',
          profile: profileData
        } 
      };
    } catch (error: any) {
      console.error('Firebase Sign In Error:', error);
      throw new Error(error.message || 'Authentication failed. Please check your credentials.');
    }
  }

  /**
   * Attempt to sign up using Firebase
   */
  async signUp(userData: { email: string; password: string; name: string; type: UserType; agentName?: string }): Promise<{ user: any }> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
      const user = userCredential.user;

      // Create user profile in Firestore
      const initialProfile = {
        email: userData.email,
        name: userData.name,
        userType: userData.type,
        agentName: userData.agentName || null,
        subscriptionTier: 'free',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', user.uid), initialProfile);

      return { user: { ...user, type: userData.type } };
    } catch (error: any) {
      console.error('Firebase Sign Up Error:', error);
      throw new Error(error.message || 'Node initialization failed.');
    }
  }
}

export const authService = new AuthService();
